import { ShieldX } from '@/components/ui/icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { InvitePreviewDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { inviteApi } from '@/api/endpoints';
import { ServerIcon } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Spinner';
import { useServers } from '@/stores/servers';
import { toast } from '@/stores/ui';

// Random codes are letters and digits; custom links can also have dashes.
const INVITE_LINK_RE = /https?:\/\/([^\s/]+)\/invite\/([A-Za-z0-9-]{3,32})(?![A-Za-z0-9-])/g;

/** Invite codes for this ClanFire instance found in a message (max 3). */
export function extractInviteCodes(content: string): string[] {
  const codes = new Set<string>();
  for (const match of content.matchAll(INVITE_LINK_RE)) {
    if (match[1] === location.host) codes.add(match[2]!);
    if (codes.size === 3) break;
  }
  return [...codes];
}

// Previews are shared by every message that links the same invite.
const previews = new Map<string, Promise<InvitePreviewDTO>>();
function loadPreview(code: string) {
  let pending = previews.get(code);
  if (!pending) {
    pending = inviteApi.preview(code);
    previews.set(code, pending);
    // Let a later render retry after transient failures.
    pending.catch(() => setTimeout(() => previews.delete(code), 30_000));
  }
  return pending;
}

type State = { status: 'loading' } | { status: 'ready'; preview: InvitePreviewDTO } | { status: 'invalid' };

export function InviteEmbed({ code, fromMe }: { code: string; fromMe: boolean }) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [joining, setJoining] = useState(false);
  const navigate = useNavigate();
  const preview = state.status === 'ready' ? state.preview : null;
  // Membership comes from the live store so the button flips to "Joined" right away.
  const joined = useServers((s) => (preview ? !!s.servers[preview.server.id] : false));

  useEffect(() => {
    let cancelled = false;
    loadPreview(code)
      .then((p) => !cancelled && setState({ status: 'ready', preview: p }))
      .catch(() => !cancelled && setState({ status: 'invalid' }));
    return () => {
      cancelled = true;
    };
  }, [code]);

  const join = async () => {
    if (!preview) return;
    if (joined) return navigate(`/app/${preview.server.id}`);
    setJoining(true);
    try {
      const server = await inviteApi.accept(code);
      useServers.getState().upsertServer(server);
      toast.success(`Welcome to ${server.name}!`);
      navigate(`/app/${server.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="mt-1.5 w-full max-w-[420px] rounded-xl border border-line bg-sidebar p-4" aria-label="Server invite">
      <p className="mb-3 text-xs font-bold tracking-wide text-fg-muted uppercase">
        {state.status === 'invalid' ? (fromMe ? 'You sent an invite, but…' : "You've been invited to join a server, but…") : fromMe ? 'You sent an invite to join a server' : "You've been invited to join a server"}
      </p>
      {state.status === 'loading' ? (
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 rounded-[30%]" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ) : state.status === 'invalid' ? (
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-[30%] bg-danger-soft text-danger">
            <ShieldX size={24} />
          </div>
          <div>
            <p className="font-bold text-danger">Invalid invite</p>
            <p className="text-sm text-fg-muted">It may have expired or reached its use limit.</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <ServerIcon server={state.preview.server} size={48} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-bold text-fg">{state.preview.server.name}</p>
            <p className="flex items-center gap-3 text-xs text-fg-muted">
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-online" /> {state.preview.onlineCount} Online
              </span>
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-offline" /> {state.preview.memberCount} {state.preview.memberCount === 1 ? 'Member' : 'Members'}
              </span>
            </p>
          </div>
          <Button size="sm" variant={joined ? 'secondary' : 'success'} loading={joining} onClick={() => void join()}>
            {joined ? 'Joined' : 'Join'}
          </Button>
        </div>
      )}
    </div>
  );
}
